import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Bell, BellRing, X } from "lucide-react";

const AP_DISTRICTS = ["Visakhapatnam", "Vijayawada", "Guntur", "Nellore", "Kurnool", "Rajahmundry", "Tirupati", "Kakinada", "Kadapa", "Anantapur", "Vizianagaram", "Eluru", "Ongole", "Srikakulam", "Machilipatnam", "Chittoor", "Nandyal", "Amaravati"];

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

const supported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

export default function PushManager() {
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [locations, setLocations] = useState(() => {
    try { return JSON.parse(localStorage.getItem("mp_push_locs") || "[]"); } catch (e) { return []; }
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported()) return;
    navigator.serviceWorker.ready.then((reg) => reg.pushManager.getSubscription()).then((sub) => setEnabled(!!sub)).catch(() => {});
  }, []);

  if (!supported()) return null;

  const toggleLoc = (d) => setLocations((l) => (l.includes(d) ? l.filter((x) => x !== d) : [...l, d]));

  const subscribe = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { toast.error("Notifications blocked in browser settings"); setBusy(false); return; }
      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/vapid-public");
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(data.publicKey),
        });
      }
      const subJson = sub.toJSON();
      await api.post("/push/subscribe", { subscription: subJson, locations });
      localStorage.setItem("mp_push_ep", subJson.endpoint);
      localStorage.setItem("mp_push_locs", JSON.stringify(locations));
      setEnabled(true); setOpen(false);
      toast.success("Notifications enabled");
    } catch (e) { toast.error("Could not enable notifications"); }
    setBusy(false);
  };

  const savePrefs = async () => {
    setBusy(true);
    try {
      const ep = localStorage.getItem("mp_push_ep");
      if (ep) await api.put("/push/preferences", { endpoint: ep, locations });
      localStorage.setItem("mp_push_locs", JSON.stringify(locations));
      toast.success("Alert areas updated"); setOpen(false);
    } catch (e) { toast.error("Could not save"); }
    setBusy(false);
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) { await api.post("/push/unsubscribe", { endpoint: sub.endpoint }); await sub.unsubscribe(); }
      localStorage.removeItem("mp_push_ep");
      setEnabled(false); setOpen(false);
      toast.success("Notifications turned off");
    } catch (e) { toast.error("Could not disable"); }
    setBusy(false);
  };

  return (
    <>
      <Button onClick={() => setOpen(true)} data-testid="enable-alerts-btn" variant="outline" className="rounded-full bg-transparent border-white/40 text-white hover:bg-white/10">
        {enabled ? <BellRing className="w-4 h-4 mr-1.5" /> : <Bell className="w-4 h-4 mr-1.5" />}
        {enabled ? "Alert Settings" : "Enable Alerts"}
      </Button>

      {open && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 px-4" data-testid="push-modal" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-heading font-bold text-lg text-brand-ink">Listing Alerts</h3>
                <p className="text-sm text-brand-muted mt-1">Pick areas to get notified when new properties go live.</p>
              </div>
              <button onClick={() => setOpen(false)} data-testid="push-modal-close" className="text-brand-muted hover:text-brand-ink"><X className="w-5 h-5" /></button>
            </div>
            <div className="mt-4 flex flex-wrap gap-1.5 max-h-48 overflow-y-auto no-scrollbar" data-testid="push-locations">
              {AP_DISTRICTS.map((d) => (
                <button key={d} type="button" onClick={() => toggleLoc(d)} data-testid={`push-loc-${d}`}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${locations.includes(d) ? "bg-brand-emerald text-white border-brand-emerald" : "border-brand-line text-brand-muted hover:border-brand-emerald"}`}>
                  {d}
                </button>
              ))}
            </div>
            <div className="mt-5 flex gap-2">
              {enabled ? (
                <>
                  <Button onClick={savePrefs} disabled={busy} className="rounded-full bg-brand-emerald flex-1" data-testid="push-save-prefs">Save Areas</Button>
                  <Button onClick={disable} disabled={busy} variant="outline" className="rounded-full text-brand-terracotta" data-testid="push-disable">Turn Off</Button>
                </>
              ) : (
                <Button onClick={subscribe} disabled={busy} className="rounded-full bg-brand-emerald flex-1" data-testid="push-enable-confirm"><Bell className="w-4 h-4 mr-1.5" />Enable Notifications</Button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
